import React, { useContext, useEffect, useRef } from 'react'
import { LocaleContext } from '../../contexts/locale'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import { QuestionState } from 'components/questions/reducer'
import { QuestionStatistics } from 'types/QuestionStatistics'
import IntlMessageFormat from 'intl-messageformat'
import { QuestionDefinition } from '*questions.yml'
import parties from 'lib/parties.yml'
import { getParty } from 'swiss-party-colors'
/** Question Block Styles */
import qbs from 'components/questionBlock/questionBlock.module.scss'
import { ContentBlock } from 'components/contentBlock'
import { Statistic } from 'components/statistics/statistic'
import { KoeppelAnswer } from 'components/statistics/koeppelAnswer'
import clsx from 'clsx'

interface StatisticsProps {
  selectedAnswers: QuestionState
  statistics: QuestionStatistics
  showStatistics: boolean
}

export const Statistics = ({
  selectedAnswers,
  statistics,
  showStatistics
}: StatisticsProps) => {
  if (!showStatistics) {
    return null
  }

  const hasScrolled = useRef(false)
  const isFirstRender = useRef(true)

  useEffect(() => {
    isFirstRender.current = false
  }, [])

  if (!hasScrolled.current && !isFirstRender.current) {
    window.location.hash = 'statistics'
    hasScrolled.current = true
  }

  const locale = useContext(LocaleContext)
  const questions = require('components/questions/questions.yml')
  const messages = require('./locales/' + locale + '.yml')
  const questionMessages = require('components/questions/locales/questions.' +
    locale +
    '.yml')
  const localisedParties = require('locales/parties/' + locale + '.yml')
  const participantsMessage = new IntlMessageFormat(
    messages.participants,
    locale + '-CH'
  )

  // prepare all options for the party statistics
  // These need special treatment, since we want to use their colors
  const partyOptions: [string, string, number, number, string][] = parties.map(
    party => {
      const stats =
        (statistics &&
          statistics.questions &&
          statistics.questions.allParties) ||
        {}
      const translatedAbbreviation = localisedParties[party]
      const partyData = getParty(locale, translatedAbbreviation)

      return [
        party,
        translatedAbbreviation,
        (stats && stats[party]) || 0,
        ((stats && stats[party]) || 0) / statistics.participants || 0,
        partyData.color
      ]
    }
  )

  return (
    <ContentBlock
      header={messages.title}
      className={clsx('basic infographic', qbs.questionBlocks)}
      id='statistics'
    >
      <p
        dangerouslySetInnerHTML={{
          __html: participantsMessage.format({
            participants: statistics.participants
          })
        }}
      />

      <Statistic
        id='allParties'
        title='Welche Parteien werden zulegen'
        allOptions={partyOptions}
        selectedAnswers={selectedAnswers}
        correctAnswers={['GLP', 'GPS']}
      />

      {questions.map((q: QuestionDefinition) => {
        // No statistics for guess questions
        if (q.selectionType === 'guess') {
          return null
        }

        // No statistics when … well, when we don't have any data at all
        if (!statistics.questions[q.id]) {
          return null
        }

        const optionsInCategory = statistics.questions[q.id]
        const allOptions: [string, string, number, number][] = q
          .options!.map(key => [
            key as string,
            questionMessages.questions[q.id].options[key] as string,
            optionsInCategory[key] || 0,
            (optionsInCategory[key] || 0) / statistics.participants
          ])
          .sort((a, b) => b[3] - a[3])

        return (
          <Statistic
            key={q.id}
            id={q.id}
            title={questionMessages.questions[q.id].question}
            allOptions={allOptions}
            selectedAnswers={selectedAnswers}
            correctAnswers={q.correct}
          />
        )
      })}

      <KoeppelAnswer />
    </ContentBlock>
  )
}
